import { useCallback, useEffect, useState } from "react";
import { Archive, Download, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";

const HOW = `${process.env.PUBLIC_URL || ""}/confirmterms-archive.png`;

export const ArchiveTab = ({ company, onChanged }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const [auto, setAuto] = useState(company?.auto_archive || { enabled: false, days: 90 });

  const load = useCallback(async () => {
    const { data } = await api.get("/company/archive");
    setItems(data);
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, [load]);

  const saveAuto = async (next) => {
    setAuto(next);
    try {
      await api.patch("/company/auto-archive", next);
      toast.success(
        next.enabled
          ? `Completed applications will archive automatically after ${next.days} days of no activity`
          : "Automatic archiving turned off"
      );
      onChanged?.();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const download = async (a) => {
    setBusy(a.id);
    try {
      const res = await api.get(`/company/archive/${a.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Archived Credit Application - ${a.customer_name}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const reactivate = async (a) => {
    setBusy(a.id);
    try {
      await api.post(`/company/customers/${a.customer_id}/reactivate`);
      toast.success(`${a.customer_name} is active again`);
      onChanged?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const purge = async (a) => {
    if (
      !window.confirm(
        `Purge the archived application for ${a.customer_name}? The PDF is deleted permanently and cannot be recovered.`
      )
    )
      return;
    setBusy(a.id);
    try {
      await api.delete(`/company/archive/${a.id}`);
      toast.success(`Purged the archived application for ${a.customer_name}`);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-8" data-testid="archive-tab">
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <img
          src={HOW}
          alt="How archiving works: click Archive, a PDF of the complete credit application is created, it is stored securely in your archive, and the application becomes inactive while the customer stays in ConfirmTerms"
          data-testid="archive-how-image"
          className="w-full rounded-md"
        />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
        data-testid="auto-archive-card"
      >
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="max-w-xl">
            <h2 className="font-display text-lg font-extrabold tracking-tight text-slate-900">
              Archive completed applications automatically
            </h2>
            <p className="mt-1.5 text-sm text-slate-500">
              Once an application has been submitted and left untouched for a while, ConfirmTerms
              can file it for you — the PDF lands in this Archive and the application goes inactive.
              Your customer stays in your account.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={auto.enabled}
            onClick={() => saveAuto({ ...auto, enabled: !auto.enabled })}
            data-testid="auto-archive-toggle"
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 ${
              auto.enabled ? "bg-blue-600" : "bg-slate-300"
            }`}
          >
            <span
              className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-[left] duration-200 ${
                auto.enabled ? "left-6" : "left-1"
              }`}
            />
          </button>
        </div>
        {auto.enabled && (
          <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="auto-archive-days">
            <span className="mr-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              After
            </span>
            {[60, 90, 180, 365].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => saveAuto({ ...auto, days: d })}
                data-testid={`auto-archive-days-${d}`}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  auto.days === d
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
                }`}
              >
                {d} days
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-5">
          <h2 className="font-display text-lg font-extrabold tracking-tight text-slate-900">
            Archived credit applications
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Every archived application is kept as a PDF you can download any time. Archived
            customers stay in your account and no longer count towards your active applications.
            <br />* - Archived credit applications may be purged by the subscribing company as
            needed
          </p>
        </div>

        {items.length === 0 ? (
          <div className="px-6 py-14 text-center" data-testid="archive-empty">
            <Archive className="mx-auto h-6 w-6 text-slate-300" />
            <p className="mt-3 font-display text-base font-bold text-slate-900">
              Nothing archived yet
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Use the Archive button on a customer to file their completed application.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((a) => (
              <div
                key={a.id}
                data-testid={`archive-row-${a.id}`}
                className="flex flex-wrap items-center justify-between gap-4 px-6 py-5"
              >
                <div>
                  <div className="font-display text-base font-bold text-slate-900">
                    {a.customer_name}
                  </div>
                  <div className="mt-1 text-sm text-slate-500">
                    Archived {(a.archived_at || "").slice(0, 10)} · {a.references_total} references ·{" "}
                    {Math.max(1, Math.round((a.size || 0) / 1024))} KB
                  </div>
                </div>
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy === a.id}
                    onClick={() => download(a)}
                    data-testid={`archive-download-${a.id}`}
                    className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" /> PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy === a.id}
                    onClick={() => reactivate(a)}
                    data-testid={`archive-reactivate-${a.id}`}
                    className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
                  >
                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Make active
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy === a.id}
                    onClick={() => purge(a)}
                    data-testid={`archive-purge-${a.id}`}
                    className="rounded-full border-red-200 text-red-600 transition-colors duration-200 hover:bg-red-50"
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Purge
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
